"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StepByStepExcellence() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const materials = [{
    name: "Premium Interior Finishes",
    description: "High-grade, low-VOC paints applied with precision to create seamless, durable surfaces for your living spaces.",
    origin: "FINISH — MATTE & SATIN",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_detail.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_detail.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_detail.png-wvc-sizes",
    alt: "Close up of a professional painter applying smooth paint to a wall"
  }, {
    name: "Weather-Resistant Exterior",
    description: "Robust, climate-tested coatings designed to protect your home's facade while enhancing its architectural character.",
    origin: "FINISH — WEATHER-SHIELD",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_home_facade.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_home_facade.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_home_facade.png-wvc-sizes",
    alt: "Professional painter working on the exterior of a modern home"
  }, {
    name: "Custom Color Consultation",
    description: "Expert guidance to select the perfect palette that complements your home's lighting, furniture, and personal style.",
    origin: "FINISH — BESPOKE",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/color_palette_swatches.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/color_palette_swatches.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/color_palette_swatches.png-wvc-sizes",
    alt: "Professional color swatches laid out on a table"
  }, {
    name: "Surface Restoration",
    description: "Meticulous preparation including sanding, patching, and priming to ensure a flawless, long-lasting final coat.",
    origin: "FINISH — PREP-PERFECT",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/surface_prep_tools.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/surface_prep_tools.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/surface_prep_tools.png-wvc-sizes",
    alt: "Professional painting tools and surface preparation equipment"
  }];
  return <section data-section-id="230" id="step-by-step-excellence" className="dark bg-background text-foreground py-20 md:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        {}
        <div ref={headerRef} className={`transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="text-center">
            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-muted-foreground">
              Our Services
            </span>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end">
            <h2 className="font-default font-light text-foreground md:col-span-8 text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] tracking-tight">
              Precision in every stroke.
            </h2>
            <p className="font-default md:col-span-4 md:text-right text-lg md:text-xl text-muted-foreground italic">
              Quality guaranteed.
            </p>
          </div>

          <div className="mt-12 md:mt-16 h-px w-full bg-border" />
        </div>

        {}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-16 md:gap-y-20">
          {materials.map((m, i) => {
          const isOffset = i === 3;
          return <MaterialCard key={m.name} index={i} isOffset={isOffset} {...m} />;
        })}
        </div>
      </div>
    </section>;
}
function MaterialCard({
  index,
  isOffset,
  name,
  description,
  origin,
  src,
  srcset,
  sizes,
  alt
}: {
  index: number;
  isOffset: boolean;
  name: string;
  description: string;
  origin: string;
  src: string;
  srcset: string;
  sizes: string;
  alt: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div data-index={index} ref={ref} className={`group flex flex-col ${isOffset ? "md:col-start-2 md:translate-y-12" : ""} transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      {}
      <div className="relative w-full overflow-hidden bg-muted">
        <div className="aspect-square w-full">
          <img src={src} data-wvc-srcset={srcset} data-wvc-sizes={sizes} alt={alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" />
        </div>
      </div>

      {}
      <div className="mt-6 h-px w-full bg-border" />

      {}
      <h3 className="font-default font-light text-foreground mt-5" style={{
      fontSize: "22px",
      lineHeight: 1.25
    }}>
        {name}
      </h3>

      {}
      <p className="font-default text-muted-foreground mt-3" style={{
      fontSize: "14px",
      lineHeight: 1.65
    }}>
        {description}
      </p>

      {}
      <div className="mt-6">
        <span className="font-mono text-[10.5px] tracking-[0.24em] uppercase text-primary">
          {origin}
        </span>
      </div>
    </div>;
}